import Phaser from 'phaser';
import { PALETTE } from './config';
import { Boot } from './scenes/Boot';
import { Preload } from './scenes/Preload';
import { BackgroundLoad } from './scenes/BackgroundLoad';
import { MainMenu } from './scenes/MainMenu';
import { NameEntry } from './scenes/NameEntry';
import { DisciplineSelect } from './scenes/DisciplineSelect';
import { RosterSelect } from './scenes/RosterSelect';
import { Training } from './scenes/Training';
import { Cosmetics } from './scenes/Cosmetics';
import { Account } from './scenes/Account';
import { LocalAuth } from './scenes/LocalAuth';
import { Arena } from './scenes/Arena';
import { ArenaDetail } from './scenes/ArenaDetail';
import { ArenaCreate } from './scenes/ArenaCreate';
import { Leaderboard } from './scenes/Leaderboard';
import { Sprint100m } from './scenes/disciplines/Sprint100m';
import { LongJump } from './scenes/disciplines/LongJump';
import { Hurdles } from './scenes/disciplines/Hurdles';
import { Javelin } from './scenes/disciplines/Javelin';
import { HighJump } from './scenes/disciplines/HighJump';
import { TripleJump } from './scenes/disciplines/TripleJump';
import { ShotPut } from './scenes/disciplines/ShotPut';
import { HammerThrow } from './scenes/disciplines/HammerThrow';
import { PoleVault } from './scenes/disciplines/PoleVault';
import { DiscusThrow } from './scenes/disciplines/DiscusThrow';
import { Weightlifting } from './scenes/disciplines/Weightlifting';
import { Swim50m } from './scenes/disciplines/Swim50m';
import { Diving } from './scenes/disciplines/Diving';
import { Archery } from './scenes/disciplines/Archery';
import { Results } from './scenes/Results';
import { RecordCelebration } from './scenes/RecordCelebration';
import { setupInstallPrompt } from './pwa/install';
import { tracking } from './game/tracker';

// Twemoji country-flag font: Windows' system emoji font renders flag emoji as 2-letter codes,
// so canvas text shows "BR" instead of 🇧🇷. This face (scoped to the regional-indicator range)
// makes flags render as flags on desktop too, once prepended to the text font stacks.
const flagFont = document.createElement('style');
flagFont.textContent = "@font-face{font-family:'Twemoji Country Flags';unicode-range:U+1F1E6-1F1FF;src:url('/assets/fonts/TwemojiCountryFlags.woff2') format('woff2');font-display:swap;}";
document.head.appendChild(flagFont);
void document.fonts.load("16px 'Twemoji Country Flags'", '🇮🇹');

// iOS Safari's WebGL bakes transparent texture pixels to black (no premultiply/loader fix helped).
// The Canvas 2D renderer composites PNG alpha correctly, always — so force it on iOS only and
// keep WebGL everywhere else. (iPadOS 13+ reports as "MacIntel" with touch, so check that too.)
const isIOS = /iP(hone|od|ad)/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1);

const config: Phaser.Types.Core.GameConfig = {
  type: isIOS ? Phaser.CANVAS : Phaser.AUTO,
  parent: 'game',
  backgroundColor: Phaser.Display.Color.IntegerToColor(PALETTE.bgBottom).rgba,
  // RESIZE: the canvas fills the screen on every device; scenes lay out responsively
  // against this.scale.width/height. devicePixelRatio caps keep mobile GPUs happy.
  scale: {
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: window.innerWidth,
    height: window.innerHeight,
  },
  // Smooth sub-pixel motion: roundPixels caused rotating rig limbs and the scrolling
  // camera to jitter/tremble. antialias keeps the vector/3D art crisp.
  render: {
    antialias: true,
    roundPixels: false,
    powerPreference: 'high-performance',
  },
  // iOS Safari bakes transparent PNG pixels to BLACK when textures are decoded via the
  // ImageBitmap path. Forcing the plain <img> (HTMLImageElement) loader preserves the alpha
  // channel, so cut-outs (athletes/logo/UI) stay transparent on iPad/iPhone.
  loader: {
    imageLoadType: 'HTMLImageElement',
  },
  // No global gravity — disciplines drive their own motion math.
  scene: [Boot, Preload, BackgroundLoad, MainMenu, NameEntry, DisciplineSelect, RosterSelect, Training, Cosmetics, Account, LocalAuth, Arena, ArenaDetail, ArenaCreate, Sprint100m, LongJump, Hurdles, Javelin, HighJump, TripleJump, ShotPut, HammerThrow, PoleVault, DiscusThrow, Weightlifting, Swim50m, Diving, Archery, RecordCelebration, Results, Leaderboard],
};

// eslint-disable-next-line no-new
new Phaser.Game(config);

// Lupo Game analytics → vibecanyon.com dashboard. game_start on load, game_end on page exit.
tracking.gameStart();
window.addEventListener('pagehide', () => tracking.gameEnd({ duration: Math.round(performance.now() / 1000) }));

// Offer install (Android one-tap / iOS hint) when not already running as an installed app.
setupInstallPrompt();

// Auto-reload once when an updated service worker takes control, so a new deploy shows
// after a single refresh instead of serving the previously precached build.
if ('serviceWorker' in navigator) {
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
}
